import { FormikValues } from 'formik'
import moment from 'moment'
import { GAIA, SECTORS } from 'src/backend-codes'
import { EFFECTIVE_DATE_DISABLED } from 'src/config'
import i18n from 'src/i18n'
import { fitem_rd, fitem_t, fitem_wr, opt, option_t, YUPS } from 'src/types'
import { ObjectShape } from 'yup/lib/object'

import {
  compare_dates,
  deep_copy,
  diff_years,
  find_option,
  get_birth_date_disabled_from,
} from './commons.utils'
import { format_number, to_date, unformat_number } from './formatters.utils'
import { sort_maximals, sort_options } from './sorter.utils'

const is_cb = (fitem: fitem_t): boolean => fitem?.type === 'CHECK_BOX'
const is_dp = (fitem: fitem_t): boolean => fitem?.type === 'DATE_PICKER'

/**
 * Converters
 */
export const to_formik_item = (fitem: fitem_rd): fitem_t => {
  const { id, label, title, tooltip, description, capture } = fitem

  const fitem_cpy: fitem_t & fitem_rd = deep_copy(fitem)

  // GENERAL CASE
  fitem_cpy.sector_descr = SECTORS[fitem_cpy.sector || '']
  fitem_cpy.type = fitem_cpy?.form_type
  fitem_cpy.options = sort_options(fitem_cpy?.form_options)
  fitem_cpy.yup_type = fitem_cpy?.default_yup_type
  fitem_cpy.yup_message = fitem_cpy?.default_yup_message
  fitem_cpy.dependencies = fitem_cpy?.dependencies || {}

  delete fitem_cpy?.form_type
  delete fitem_cpy?.form_options
  delete fitem_cpy?.default_yup_type
  delete fitem_cpy?.default_yup_message

  if (label?.includes('.')) {
    fitem_cpy.label = i18n.t(label)
  }
  if (title) {
    fitem_cpy.title = i18n.t(title)
  }
  if (tooltip) {
    fitem_cpy.tooltip = i18n.t(tooltip)
  }
  if (description) {
    fitem_cpy.description = i18n.t(description)
    fitem_cpy.classes_description = 'c-7f7f7f'
  }
  if (fitem_cpy?.hidden || !fitem_cpy.capture) {
    fitem_cpy.capture = ''
  }

  // SPECIAL CASES
  const today = moment().format('DD/MM/YYYY')
  if (id?.includes('effective_date')) {
    fitem_cpy.capture = today
    fitem_cpy.disabled_from = today
    if (EFFECTIVE_DATE_DISABLED) {
      fitem_cpy.disabled_to = moment().add(2, 'M').format('DD/MM/YYYY')
    }
  }
  if (id?.includes('registration_date') || id?.includes('first_matriculation')) {
    fitem_cpy.disabled_from = today
  }
  if (id?.includes('birth_date')) {
    fitem_cpy.disabled_from = get_birth_date_disabled_from()
  }
  if (id?.includes('certificate_expiration_date')) {
    fitem_cpy.disabled_to = moment().subtract(5, 'year').format('DD/MM/YYYY')
  }
  if (id?.includes('maximum_compensation') || id?.includes('maximum_per_claim')) {
    fitem_cpy.options = sort_maximals(
      fitem?.form_options?.map(({ key, val }) => ({
        key,
        val: format_number(val) || '',
      }))
    )
  }
  if (description === 'ts2.vehicle_absent_description') {
    fitem_cpy.classes_description = 'c-primary'
  }
  if (is_cb(fitem_cpy)) {
    if (capture === 'S' || capture === 'SI') fitem_cpy.capture = 'true'
    if (capture === 'N' || capture === 'NO') fitem_cpy.capture = 'false'
  }
  if (id?.includes('fs1a__taxcode') || fitem_cpy?.yup_type === 'YPLATE') {
    fitem_cpy.uppercase = true
  }

  // Add Analytics - CM 3423
  if (id?.includes('is1__vehicle_type')) {
    fitem_cpy?.options?.forEach(
      (option) =>
        (option.gtm_class =
          option?.key === 'car'
            ? 'gtm-car'
            : option?.key === 'moped'
            ? 'gtm-moped'
            : option?.key === 'others'
            ? 'gtm-others'
            : '')
    )
  }

  if (id?.includes('is3__plate_present')) {
    fitem_cpy?.options?.forEach(
      (option) =>
        (option.gtm_class =
          option?.key === 'yes' ? 'gtm-plate' : option?.key === 'no' ? 'gtm-no-plate' : '')
    )
  }

  return fitem_cpy
}

export const to_formik_write = (fitem: fitem_t): fitem_wr[] => {
  const fpool: fitem_wr[] = []
  const id = fitem?.id
  const capt = fitem?.capture

  if (is_option_based(fitem)) {
    const res: fitem_wr = {
      id,
      key: capt,
      val: find_option(fitem?.options, capt)?.val,
    }
    // SPECIAL CASES
    if (id?.includes('category')) {
      capt?.split('-')?.forEach((field, idx) => {
        fpool.push({
          ...res,
          id_gaia: GAIA[`${id}_id_${idx}`],
          campo_gaia: field,
        })
      })
      fpool.push({ ...res, id_gaia: GAIA[`${id}_tx`], campo_gaia: res.val })
    }
    //
    else if (id?.includes('maximum_compensation') || id?.includes('maximum_per_claim')) {
      fpool.push({
        ...res,
        id_gaia: GAIA[`${id}_id`],
        campo_gaia: res.key,
        val: unformat_number(res.val),
      })
    }
    // GENERAL CASE
    else {
      fpool.push({ ...res, id_gaia: GAIA[`${id}_id`], campo_gaia: res.key })
      fpool.push({ ...res, id_gaia: GAIA[`${id}_tx`], campo_gaia: res.val })
    }
  } else {
    const res: fitem_wr = {
      id,
      val: capt,
      campo_gaia: capt,
    }
    // SPECIAL CASES
    if (id?.includes('purchase_date')) {
      const id =
        fitem?.sector === '1001-1' ? `ts2__car__years_possession` : 'ts2__atx__years_possession'
      const val = `${diff_years(moment(capt, 'DD/MM/YYYY'))}`.replace('.', ',')
      fpool.push({ id, val, campo_gaia: val, id_gaia: GAIA[id] })
    }
    if (
      (id?.includes('vehicle_displacement') && fitem?.sector !== '1001-1') ||
      id?.includes('ts2__trk__quintals') ||
      id?.includes('ts2__trk__quintals_trailer') ||
      id?.includes('ts2__wt1__hull_length') ||
      id?.includes('ts2__wt1__vehicle_kw_power')
    ) {
      fpool.push({ ...res, id_gaia: GAIA[`${id}_id`] })
      fpool.push({ ...res, id_gaia: GAIA[`${id}_tx`] })
    }
    // GENERAL CASE
    else {
      fpool.push({ ...res, id_gaia: GAIA[id] })
    }
  }
  return fpool
}

export const to_formik_post = (fpool: fitem_t[] = []): fitem_wr[] => {
  const fpool_post: fitem_wr[] = []
  fpool
    ?.filter((fitem_) => fitem_?.capture && !fitem_?.hidden)
    ?.forEach((fitem_) => fpool_post.push(...to_formik_write(fitem_)))
  return fpool_post
}

export const to_formik_schema = (fpool: fitem_t[] = []): ObjectShape => {
  const schema: FormikValues = {}
  fpool?.forEach((fitem) => {
    // Specific Fix 50cc
    if (fitem?.id?.includes('ts2__mop__vehicle_displacement') && fitem?.entity === '22') {
      fitem = { ...fitem, yup_type: 'Y50CC' }
    }
    if (fitem.id?.includes('ts2__car__registration_date')) {
      const purchase_date = fpool.find((fitem_) => fitem_?.id?.includes('ts2__car__purchase_date'))
      if (purchase_date) {
        purchase_date.disabled_to = fitem.capture
      }
    }
    if (fitem.id?.includes('ts2__atx__first_matriculation')) {
      const purchase_date = fpool.find((fitem_) => fitem_?.id?.includes('ts2__atx__purchase_date'))
      if (purchase_date) {
        purchase_date.disabled_to = fitem.capture
      }
    }

    const fid_ = fitem?.id
    schema[fid_] = YUPS(fitem?.yup_message)[fitem?.yup_type || 'YSTR']

    // Setup the "required" property
    if (fitem?.required && !fitem?.disabled && !fitem?.hidden) {
      if (is_cb(fitem)) {
        schema[fid_] = schema[fid_]?.oneOf([true], i18n.t('commons.required'))
      } else {
        schema[fid_] = schema[fid_]?.required(i18n.t('commons.required'))
      }
      // Date-picker contraints
      if (is_dp(fitem) && (fitem?.disabled_from || fitem?.disabled_to)) {
        schema[fid_] = schema[fid_]?.test({
          message: i18n.t('commons.invalid_date'),
          test: (value: string) => {
            if (fid_?.includes('effective_date')) {
              if (fitem?.disabled_from && fitem?.disabled_to) {
                const valid =
                  !!value &&
                  compare_dates(to_date(fitem?.disabled_from), to_date(value)) < 1 &&
                  compare_dates(to_date(fitem?.disabled_to), to_date(value)) > -1
                return valid
              } else if (fitem?.disabled_from) {
                return !!value && compare_dates(to_date(fitem?.disabled_from), to_date(value)) < 1
              } else if (fitem?.disabled_to) {
                return !!value && compare_dates(to_date(fitem?.disabled_to), to_date(value)) > -1
              }
            } else {
              return fitem?.disabled_from
                ? !!value && compare_dates(to_date(fitem?.disabled_from), to_date(value)) > -1
                : !!value && compare_dates(to_date(fitem?.disabled_to), to_date(value)) < 1
            }
          },
        })
      }
    }
  })
  return schema
}

/**
 * Finders
 */
export const find_fitem = (fpool: fitem_t[] = [], id_ = ''): opt<fitem_t> => {
  return fpool?.find(({ id }) => id?.includes(id_))
}

export const find_fitem_idx = (fpool: fitem_t[] = [], id_ = ''): number => {
  return fpool?.findIndex(({ id }) => id?.includes(id_))
}

export const find_foption = (fpool: fitem_t[] = [], id?: string): opt<option_t> => {
  const fitem_ = find_fitem(fpool, id)
  return find_option(fitem_?.options, fitem_?.capture)
}

export const filter_fstep = (fpool: fitem_t[] = [], step?: string): fitem_t[] => {
  return fpool?.filter(({ id }) => id?.split('__')[0] === step) || []
}

export const is_option_based = (fitem: fitem_t): boolean => {
  return fitem?.type === 'SELECT_MENU' || fitem?.type === 'RADIO_GROUP'
}

/**
 * Updaters
 */
export const inject_company = (fpool: fitem_t[] = [], company = '022'): fitem_t[] => {
  const newpool = deep_copy(fpool)
  const idx_1 = find_fitem_idx(newpool, 'ts1b__company')
  const idx_2 = find_fitem_idx(newpool, 'fs1b__company')
  if (idx_1 !== -1) newpool[idx_1].capture = company
  if (idx_2 !== -1) newpool[idx_2].capture = company
  return newpool
}

export const inject_options = (fpool: fitem_t[] = [], id_ = '', opts?: option_t[]): fitem_t[] => {
  const newpool = deep_copy(fpool)
  const idx = find_fitem_idx(newpool, id_)
  if (idx !== -1) {
    newpool[idx].options = opts
  }
  return newpool
}

export const update_html_field = (field_name: string, action: string): void => {
  const input = document.getElementById(field_name) as HTMLInputElement
  if (input) {
    switch (action) {
      case 'disable':
        if (!input.disabled) {
          input.disabled = true
          input.classList.add('bg-fafafa')
        }
        break
      case 'enable':
        if (input.disabled) {
          input.disabled = false
          input.classList.remove('bg-fafafa')
        }

        break
      default:
    }
  }
}
